<template>
  <div :class="$style.contact">
    <MoleculesConsumerHeading title="Contact" sub-title="お問い合わせ" />
    <form :class="$style.contact_form" @submit.prevent="gotoConfirm">
      <div :class="$style.message">
        <p :class="$style.alert_message">
          現在大変多くのお問い合わせを頂戴しており、回答にお日にちをいただいております。ご不便をおかけし申し訳ございませんが、何卒ご理解いただきますようお願いいたします。
        </p>
        <div v-if="isProductGuide">
          <p>【ご訪問ありがとうございます】 </p>
          <p>
            こちらはTENTIALお問い合わせ窓口になります。 お手元の商品についてご不明点やご相談、
            店舗でご購入いただいたお客様、ギフトで受け取られたお客様の交換ご希望の場合もこのままお進みください。
          </p>
        </div>
        <div>
          <p>【携帯電話キャリアメールをご利用のお客様】 </p>
          <p>
            キャリアメールをご利用をお客様へは<code>support-tential@au.com</code>より返信させていただきます。メール受信設定のご確認・変更をお願いいたします。
          </p>
        </div>
        <p v-if="!isProductGuide">
          法人のお客様のお問い合わせは
          <AtomsConsumerLink url="https://corp.tential.jp/contact">弊社コーポレートサイト</AtomsConsumerLink>
          よりお願いいたします。
        </p>
      </div>

      <MoleculesConsumerFormItem label="required">
        <template #title>お問い合わせ種別</template>
        <AtomsConsumerSelect
          v-model="state.contact.category"
          :options="objectToAtomsConsumerSelectOptions(ContactCategory)"
          placeholder="選択してください"
        />
        <template #footer-description>
          <p v-if="state.contact.category === 'before_delivery'" :class="$style.footer_description">
            ご注文確定後、注文内容の変更（お届け日、住所、名前、ラッピング等）は承ることができません。<br />
            変更をご希望の場合は、
            <AtomsConsumerLink :class="$style.footer_description_link" :url="`/mypage/orders/`">
              購入履歴
            </AtomsConsumerLink>
            より該当のご注文をキャンセルいただき、再注文をお願いいたします。
          </p>
        </template>
      </MoleculesConsumerFormItem>

      <div v-if="!isSigned && typeOfEnquiryForStoreOrder" id="btn-box" :class="$style.btn_box">
        <AtomsConsumerLink :url="`/sign_up?next=${redirectPath}`">
          <AtomsConsumerButton variant="primary" block>新規会員登録</AtomsConsumerButton>
        </AtomsConsumerLink>
        <AtomsConsumerLink :url="`/sign_in?next=${redirectPath}`">
          <AtomsConsumerButton variant="transparent" block>ログイン</AtomsConsumerButton>
        </AtomsConsumerLink>
      </div>

      <MoleculesConsumerFormItem v-if="state.contact.category !== 'before_order'">
        <template #title>ご購入チャネル</template>
        <AtomsConsumerSelect
          v-model="state.contact.channel"
          :options="purchaseChannelsValue"
          placeholder="選択してください"
        />
        <template #footer-description>
          <p v-if="state.contact.channel === '実店舗'" :class="$style.footer_description">
            【ご購入時に注文連携・マイル付与できなかったお客様】 <br />
            お問い合わせ種別より「店舗注文（ログイン必須）」を選択して下さい。ログインされていない方は、ログインを行ってください。会員登録がお済みでない方は、新規会員登録をお願いいたします。
          </p>
        </template>
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem label="required">
        <template #title>お名前</template>
        <AtomsConsumerInput
          id="email"
          v-model="state.contact.name"
          autocomplete="name"
          name="name"
          required
          placeholder="例）鈴木花子"
        />
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem label="required">
        <template #title>メールアドレス</template>
        <AtomsConsumerInput
          id="email"
          v-model="state.contact.email"
          autocomplete="email"
          name="email"
          required
          placeholder="例）tential@e-mail.com"
        />
        <AtomsConsumerInput
          id="email"
          v-model="state.contact.confirmEmail"
          autocomplete="off"
          name="confirmEmail"
          required
          placeholder="メールアドレス(確認)"
        />
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem v-if="state.contact.category" label="required">
        <template #title>お問い合わせ内容</template>
        <AtomsConsumerSelect
          v-model="state.contact.sub_category"
          :options="objectToAtomsConsumerSelectOptions(subCategoryName || {})"
          placeholder="選択してください"
        />
        <template #footer-description>
          <p
            v-if="
              state.contact.sub_category === 'about_the_support' &&
              state.contact.channel === 'TENTIAL公式オンラインストア'
            "
            :class="$style.footer_description"
          >
            オンラインストアでご注文の商品の返品/交換をご希望の場合は、
            <AtomsConsumerLink :url="`/return_or_exchange`" :class="$style.footer_description_link">
              「返品交換申請フォーム」
            </AtomsConsumerLink>
            よりお手続きください。
          </p>
        </template>
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem v-if="state.contact.sub_category === 'about_the_support'" label="required">
        <template #title>{{ supportLabel }}</template>
        <AtomsConsumerSelect
          v-model="state.contact.support_status"
          :options="objectToAtomsConsumerSelectOptions(getSupportStatus || {})"
          placeholder="選択してください"
        />
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem v-if="state.contact.sub_category === 'about_the_product'" label="required">
        <template #title>商品名</template>
        <AtomsConsumerInput
          v-model="state.contact.product_name"
          name="product_name"
          :required="state.contact.sub_category === 'about_the_product'"
          placeholder="例）BAKUNE スウェットシャツ"
        />
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem>
        <template #title>注文ID</template>
        <AtomsConsumerInput v-model="state.contact.order_id" name="order_id" :disabled="state.disableOrderId" />
      </MoleculesConsumerFormItem>

      <MoleculesConsumerFormItem label="required">
        <template #title>お問い合わせ詳細</template>
        <AtomsConsumerTextarea
          id="contact_form_text_area"
          v-model="state.contact.content"
          name="content"
          required
          rows="8"
          placeholder="お問い合わせの詳細をご記入ください"
        />
      </MoleculesConsumerFormItem>

      <div id="contact_form_suggest_area" :class="$style.suggest_area" />

      <MoleculesConsumerFormItem>
        <template #title>詳細画像(スクリーンショット等)</template>
        <InputFileMultiple
          :media-src="state.contact.image_urls"
          placeholder-button-text="ファイルを選択"
          @on-change="selectImg"
          @on-remove="removeImg"
        />

        <template #footer-description>
          <p v-if="state.contact.channel === '実店舗'" :class="$style.footer_description">
            領収書をお持ちの方は、領収書の画像をアップロードしてください。<br />
            領収書がない方は、商品をご購入した店舗、時間などを「お問い合わせ詳細」に記載ください。
          </p>
        </template>
      </MoleculesConsumerFormItem>

      <AtomsConsumerCheckbox v-model="state.confirm" name="confirm">
        <AtomsConsumerLink url="https://corp.tential.jp/privacy" target="_blank" :class="$style.link">
          プライバシーポリシー
        </AtomsConsumerLink>
        を確認しました
      </AtomsConsumerCheckbox>

      <AtomsConsumerButton variant="primary" type="submit" :disabled="!state.confirm" block>
        確認画面へ
      </AtomsConsumerButton>
    </form>
  </div>
</template>

<script setup lang="ts">
import VueScrollTo from "vue-scrollto"
import {
  purchaseChannels,
  purchaseChannelsForGuide,
  ContactCategory,
  ContactSubCategoryBeforeOrder,
  ContactSubCategoryBeforeDelivery,
  ContactSubCategoryAfterDelivery,
  ContactSupportStatusBeforeDelivery,
  ContactSupportStatusAfterDelivery,
} from "@tential/ec-gql-schema/master/contact"
import { objectToAtomsConsumerSelectOptions } from "@tential/consumer-component/utils"
import { useOwnUserOneQuery } from "~/gql/urql.generated"
import { useToast } from "~/state"
import { useStore } from "~/stores"
import { useCustomMeta } from "~/composables/meta"
import { ContactStoreState } from "~/types/contact"
import { EventHandler } from "~/types/events"
import { emailValidation } from "~/utils/functions/validate"
import { useLocalUser } from "~/composables/useLocalUser"
import { useKarakuri } from "~/composables/useKarakuri"

interface State {
  contact: ContactStoreState
  confirm: boolean
  disableOrderId: boolean
}

const store = useStore()
const router = useRouter()
const toast = useToast()
const route = useRoute()
const customMeta = useCustomMeta()
const { getSiteId } = useSite()
const orderId = route.query.orderId as string
const siteId = getSiteId()
const redirectPath = encodeURIComponent(route.fullPath)
const { getLocalUserId } = useLocalUser()

// karakuriの管理
const { initializeKarakuri } = useKarakuri()

const isSigned = computed(() => {
  return store.user.isSigned
})
const typeOfEnquiryForStoreOrder = computed(() => {
  return state.contact.category === "store_order"
})

const { executeQuery: ownUserOne } = useOwnUserOneQuery()

const productGuideQuery = computed(() => {
  return typeof route.query.from === "string" ? route.query.from : ""
})

const isProductGuide = computed(() => {
  return productGuideQuery.value === "product_guide_book"
})

const purchaseChannelsValue = computed(() => {
  if (isProductGuide.value) return purchaseChannelsForGuide
  return purchaseChannels
})

const state: State = reactive({
  contact: {
    name: "",
    email: "",
    confirmEmail: "",
    channel: "",
    type_id: 0,
    type_name: "",
    category: "before_order",
    sub_category: "about_the_product",
    support_status: "",
    product_name: "",
    order_id: "",
    content: "",
    files: [],
    site_id: "",
    local_user_id: "",
    image_urls: [],
    is_product_guide: false,
  },
  confirm: false,
  disableOrderId: false,
})

const subCategoryName = computed(() => {
  if (state.contact.category === "before_order") return ContactSubCategoryBeforeOrder
  else if (state.contact.category === "before_delivery") return ContactSubCategoryBeforeDelivery
  else if (state.contact.category === "after_delivery" || state.contact.category === "store_order")
    return ContactSubCategoryAfterDelivery
})

const supportLabel = computed(() => {
  if (state.contact.category === "before_delivery") return "キャンセル、返品、交換"
  else if (state.contact.category === "after_delivery" || state.contact.category === "store_order") return "返品、交換"
  return ""
})

const getSupportStatus = computed(() => {
  if (state.contact.category === "before_delivery") return ContactSupportStatusBeforeDelivery
  else if (state.contact.category === "after_delivery" || state.contact.category === "store_order")
    return ContactSupportStatusAfterDelivery
})

const gotoConfirm: EventHandler = () => {
  if (isErrorCheck()) return
  state.contact.is_product_guide = isProductGuide.value
  store.setContact(state.contact)
  router.push(`contact/confirm`)
}

// Todo: リアルタイムバリデーションする
const isErrorCheck = (): boolean => {
  const emailValidationResult = emailValidation(state.contact.email)
  let errorMessage = ""
  let toastMessage = ""
  let scrollToElement = ""
  if (!state.contact.name) {
    errorMessage = "お名前"
  } else if (!state.contact.email) {
    errorMessage = "メールアドレス"
  } else if (!state.contact.confirmEmail) {
    errorMessage = "メールアドレス(確認)"
  } else if (state.contact.email !== state.contact.confirmEmail) {
    toastMessage = "メールアドレスが一致しません"
    scrollToElement = "#email"
  } else if (emailValidationResult.isPassed === false) {
    toastMessage = "メールアドレスの形式が正しくありません"
    scrollToElement = "#email"
  } else if (!state.contact.category) {
    errorMessage = "お問い合わせ種別"
  } else if (!state.contact.sub_category) {
    errorMessage = "お問い合わせ内容"
  } else if (state.contact.sub_category === "about_the_support" && !state.contact.support_status) {
    errorMessage = "キャンセル、返品、交換"
  } else if (state.contact.sub_category === "about_the_product" && !state.contact.product_name) {
    errorMessage = "製品の名前"
  } else if (!state.contact.content) {
    errorMessage = "お問い合わせ詳細"
  } else if (!state.confirm) {
    toastMessage = "個人情報の取り扱いについての確認がありません"
  } else if (state.contact.category === "store_order" && !isSigned.value) {
    toastMessage = "お問い合わせ種別が店舗注文の場合、ログインまたは新規会員登録が必要です"
    scrollToElement = "#btn-box"
  } else if (state.contact.category === "store_order" && !isSigned.value) {
    toastMessage = "お問い合わせ種別が店舗注文の場合、ログインまたは新規会員登録が必要です"
    scrollToElement = "#btn-box"
  }

  if (scrollToElement)
    VueScrollTo.scrollTo(scrollToElement, 100, {
      offset: -180,
    })

  if (errorMessage) {
    toast?.showErrorToasted(`${errorMessage}が入力されていません`)
    return true
  } else if (toastMessage) {
    toast?.showErrorToasted(toastMessage)
    return true
  }

  return false
}

const selectImg: EventHandler = (file, url) => {
  state.contact.image_urls.push(url)
  state.contact.files.push(file)
}

const removeImg: EventHandler = (index) => {
  state.contact.files.splice(index, 1)
  state.contact.image_urls.splice(index, 1)
}

onMounted(async () => {
  const { data } = await ownUserOne()
  const contact = {
    ...store.contact,
    order_id: orderId || store.contact.order_id,
    site_id: siteId,
  }
  contact.name = data.value?.ownUserOne?.name || contact.name
  contact.email = data.value?.ownUserOne?.email || contact.email
  contact.confirmEmail = data.value?.ownUserOne?.email || contact.confirmEmail
  if (isProductGuide.value) {
    contact.is_product_guide = isProductGuide.value
    contact.category = "after_delivery"
    contact.sub_category = "about_the_support"
    contact.channel = "ギフト"
    contact.support_status = "return"
  }
  state.contact = contact
  if (orderId) state.disableOrderId = true
  state.contact.local_user_id = getLocalUserId()

  if (route.fullPath.includes("size_check")) {
    const genderMapping: { [key: string]: string } = {
      male: "男性",
      female: "女性",
      "": "不明",
    }
    const genderValue = Array.isArray(route.query.gender) ? route.query.gender[0] : route.query.gender
    const mappedGender = genderValue ? genderMapping[genderValue] || "不明" : "不明"
    const productId = decodeURIComponent(route.query.product_id as string)
    const height = decodeURIComponent(route.query.height as string)
    const weight = decodeURIComponent(route.query.weight as string)
    const footSize = decodeURIComponent(route.query.foot_size as string)
    const lines = [
      `商品ID：${productId}`,
      mappedGender !== "不明" ? `性別：${mappedGender}` : undefined,
      height !== "null" ? `身長：${height}cm` : undefined,
      weight !== "null" ? `体重：${weight}kg` : undefined,
      footSize !== "null" ? `足のサイズ：${footSize}cm` : undefined,
      "推奨サイズを教えて下さい。",
    ]
    const formattedLines = lines.filter(Boolean).join("\n")

    state.contact.product_name = decodeURIComponent(route.query.product_name as string)
    state.contact.category = "before_order"
    state.contact.sub_category = "about_the_product"
    state.contact.channel = "TENTIAL公式オンラインストア"
    state.contact.content = formattedLines
  }

  // karakuriの初期化
  initializeKarakuri("contact_form_suggest_area")
})

useHead(
  customMeta.fetch({
    title: "お問い合わせ",
    description:
      "TENTIALの公式オンラインストア。「どんなに身体能力が違っても、成長という喜びは平等だ。」をコンセプトに、あなたの日常とスポーツに新たな価値を創造していブランドです。データとテクノロジーを駆使して、ユーザーに合わせた最適な商品展開をしています。",
  }),
)

onBeforeRouteLeave((_, __, next) => {
  store.setContact(state.contact)
  next()
})
</script>

<style scoped module lang="scss">
.contact {
  .contact_form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1rem;
    @include md {
      max-width: $max-width-sm;
      margin: 1rem auto;
    }
    .message {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      code {
        font-family: $main-english-font;
      }
      .alert_message {
        color: $danger;
        font-weight: bold;
      }
    }
    .footer_description {
      font-size: $font-size-12;
      @include md {
        font-size: $font-size-14;
      }
    }
    a {
      text-decoration: underline;
      &.footer_description_link {
        color: $primary;
      }
    }
  }
  .btn_box {
    display: flex;
    gap: 1rem;
    a {
      text-decoration: none;
    }
  }
  .suggest_area {
    height: 10rem;
  }
}
</style>
